/**
 * Environment Configuration and Validation
 * Validates required environment variables and provides safe defaults
 */

// Validate required environment variables
const validateEnvironment = () => {
    const errors = [];
    const warnings = [];

    // Required in production (unless same-origin deploy, e.g. Elastic Beanstalk single app)
    if (process.env.NODE_ENV === 'production') {
        const apiUrl = process.env.REACT_APP_API_URL;
        if (apiUrl !== undefined && apiUrl !== '') {
            const trimmed = String(apiUrl).trim();
            // Relative paths (e.g. /api, /) are valid: CRA proxy or same-origin /api
            if (trimmed.startsWith('/')) {
                if (trimmed !== '/api' && trimmed !== '/' && !trimmed.startsWith('/api/')) {
                    warnings.push('REACT_APP_API_URL is relative; ensure it targets your API (usually /api)');
                }
            } else {
                try {
                    const url = new URL(trimmed);
                    if (url.protocol !== 'https:') {
                        warnings.push('REACT_APP_API_URL should use HTTPS in production');
                    }
                } catch (e) {
                    errors.push('REACT_APP_API_URL must be a valid absolute URL or a path starting with /');
                }
            }
        }
        // Empty or unset = same-origin /api via normalizeApiUrl (allowed for single-server deploy e.g. EB)
    }

    // Warn if Sentry is not configured in production
    if (process.env.NODE_ENV === 'production' && !process.env.REACT_APP_SENTRY_DSN) {
        warnings.push('REACT_APP_SENTRY_DSN not set - error tracking will be disabled in production');
    }

    // Log errors and warnings
    if (errors.length > 0) {
        console.error('❌ Environment Validation Errors:');
        errors.forEach(error => console.error(`   - ${error}`));
        throw new Error('Environment validation failed');
    }

    if (warnings.length > 0 && process.env.NODE_ENV === 'production') {
        console.warn('⚠️  Environment Validation Warnings:');
        warnings.forEach(warning => console.warn(`   - ${warning}`));
    }

    return { errors, warnings };
};

// Ensure API base URL ends with /api so paths like /auth/login become /api/auth/login
function normalizeApiUrl(value) {
    if (value === undefined || value === '') return '/api';
    const s = String(value).trim();
    // "/" alone → /api (matches package.json proxy + Express /api routes)
    if (s === '/' || s === '/api') return '/api';
    if (s.endsWith('/api/')) return s.replace(/\/+$/, '');
    if (s.endsWith('/api')) return s.replace(/\/+$/, '');
    return s.replace(/\/+$/, '') + '/api';
}

// Environment configuration
const env = {
    // API base URL. Use REACT_APP_API_URL; if it's a full URL (e.g. https://localhost:5001), we append /api so login hits /api/auth/login.
    API_URL: normalizeApiUrl(process.env.REACT_APP_API_URL),
    
    // Sentry Configuration
    SENTRY_DSN: process.env.REACT_APP_SENTRY_DSN || '',
    SENTRY_ENABLE_DEV: process.env.REACT_APP_SENTRY_ENABLE_DEV === 'true',
    
    // Application Info
    VERSION: process.env.REACT_APP_VERSION || '1.0.0',
    GIT_SHA: process.env.REACT_APP_GIT_SHA || '',
    BUILD_DATE: process.env.REACT_APP_BUILD_DATE || '',
    
    // Feature Flags
    DEBUG: process.env.REACT_APP_DEBUG === 'true',
    ANALYTICS_ID: process.env.REACT_APP_ANALYTICS_ID || '',
    
    // Environment
    NODE_ENV: process.env.NODE_ENV || 'development',
    IS_PRODUCTION: process.env.NODE_ENV === 'production',
    IS_DEVELOPMENT: process.env.NODE_ENV === 'development',
};

// Validate on module load (only in production)
if (process.env.NODE_ENV === 'production') {
    try {
        validateEnvironment();
    } catch (error) {
        // Don't crash the app, just log the error
        console.error('Environment validation failed:', error.message);
    }
}

// Get environment summary (safe - no secrets)
export const getEnvSummary = () => ({
    NODE_ENV: env.NODE_ENV,
    API_URL: env.API_URL,
    VERSION: env.VERSION,
    SENTRY_ENABLED: !!env.SENTRY_DSN,
    DEBUG: env.DEBUG,
});

export default env;

